/**
 * What the next-phrase request carries (contract, section 1.2): each hand's
 * state as the string `<midi>:<finger>:<rested>`, with `rested` as 0 or 1 (for
 * example `66:3:0`), in one optional query parameter per hand, and the
 * transposition in force, in whole semitones. A state names the sounding key
 * the hand is on. Its shape is not sent: it never changes whether a phrase can
 * follow.
 */
import type { Finger, Hand, HandState } from './types';

/** The query parameter of each hand's state on the next-phrase request. */
export const HAND_STATE_PARAMETERS: Readonly<Record<Hand, string>> = {
  rh: 'rhState',
  lh: 'lhState',
};

/** The query parameter of the transposition on the next-phrase request. */
export const TRANSPOSITION_PARAMETER = 'transposition';

const WIRE_PATTERN = /^(-?[0-9]+):([1-5]):([01])$/;
const WHOLE_NUMBER_PATTERN = /^-?[0-9]+$/;

export function encodeHandState(state: HandState): string {
  return `${state.midi}:${state.finger}:${state.rested ? 1 : 0}`;
}

/** The hand state of a wire string, or null when it does not parse. */
export function decodeHandState(text: string): HandState | null {
  const parts = WIRE_PATTERN.exec(String(text));
  return parts === null
    ? null
    : { midi: Number(parts[1]), finger: Number(parts[2]) as Finger, rested: parts[3] === '1' };
}

/** The transposition of a request: the whole number sent, 0 when it is left
 *  out or is not one. */
export function decodeTransposition(text: unknown): number {
  const sent = typeof text === 'number' ? `${text}` : text;
  return typeof sent === 'string' && WHOLE_NUMBER_PATTERN.test(sent) ? Number(sent) : 0;
}

/** The query parameters that carry these hand states; a hand without a state
 *  is left out. */
export function handStateParameters(
  states: Partial<Record<Hand, HandState | null>>,
): Record<string, string> {
  return (Object.keys(HAND_STATE_PARAMETERS) as Hand[]).reduce<Record<string, string>>(
    (parameters, hand) => {
      const state = states[hand];
      return state === undefined || state === null
        ? parameters
        : { ...parameters, [HAND_STATE_PARAMETERS[hand]]: encodeHandState(state) };
    },
    {},
  );
}

/** Everything a next-phrase request says about the hands: the states that are
 *  known, and the transposition in force, which is always sent. */
export function phraseRequestParameters(
  states: Partial<Record<Hand, HandState | null>>,
  transposition: number,
): Record<string, string> {
  if (!Number.isInteger(transposition)) {
    throw new RangeError(`a transposition is a whole number of semitones, not ${transposition}`);
  }
  return { ...handStateParameters(states), [TRANSPOSITION_PARAMETER]: `${transposition}` };
}
